import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import { Dropdown } from "antd";
import { FaChevronDown } from "react-icons/fa";

// components
import InfoTooltip from "@/components/InfoTooltip";
import HeaderRegionCurrencyDropdownContent from "./HeaderRegionCurrencyDropdownContent";

// helpers
import { CURRENCY_TOOLTIP_INFO } from "@/helpers/currencyUtils";
import currencies from "@/api/query/currencies";
import useCurrency from "@/hooks/useCurrency";
import global from "@/api/query/global";

// interfaces
import { TRegion, TCurrencies } from "./IHeaderRegionCurrencyDropdownContent";

// assets
import EarthGlobeIcon from "@/components/icons/EarthGlobeIcon";
import styles from "@/assets/styles/components/header-region-currency-dropdown.module.scss";


const HeaderRegionCurrencyDropdown = () => {
  const [currentCurrency] = useCurrency();

  const [defaultRegion, setDefaultRegion] = useState<TRegion | null>(null);
  const [defaultCurrencies, setDefaultCurrencies] = useState<TCurrencies | null>(null);

  const { lang } = useParams();

  const currenciesListUtils = currencies.useCurrenciesList();
  const { data: regions } = global.useGeographicRegion();
  
  const regionOptions = (regions || []).map(item => ({
    label: item.name,
    value: `/${item.code}`,
    code: item.code,
  }));

  const currenciesOptions = (currenciesListUtils.data || []).map(item => ({
    label: item.code,
    value: item.code,
    currencyObj: item
  }));

  // effects
  useEffect(() => {
    const currentCurrencyFromOptions = currenciesOptions.find(item => item.value === currentCurrency) || null;

    setDefaultCurrencies(currentCurrencyFromOptions);
  }, [currentCurrency, currenciesListUtils?.data])

  useEffect(() => {
    const currentRegion = regionOptions.find((region) => region.code === lang) || regionOptions[0];

    setDefaultRegion(currentRegion);
  }, [])

  // render
  const renderContent = () => {
    if (!defaultRegion || !defaultCurrencies) {
      return null
    }

    return (
      <HeaderRegionCurrencyDropdownContent
        currenciesOptions={currenciesOptions}
        region={defaultRegion}
        currency={defaultCurrencies}
      />
    )
  }

  return (
    <div className={styles["locl-header-region-currency-dropdown"]} data-testid="locl-header-region-currency-dropdown">
      <Dropdown
        getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
        overlayClassName={styles["locl-header-region-currency-dropdown__dropdown"]}
        dropdownRender={renderContent}
        trigger={["click"]}
        placement={"bottom"}
        destroyPopupOnHide
      >
        <div className={styles["locl-header-region-currency-dropdown__button"]} data-testid="locl-header-region-currency-dropdown-button">
          <EarthGlobeIcon className={styles["locl-header-region-currency-dropdown__button-globe"]}/>
          <div className={styles["locl-header-region-currency-dropdown__button-info"]}>
            <div className="locl-header-region-currency-dropdown__button-info__region" data-testid="locl-header-region-currency-dropdown-button-region">{defaultRegion?.label || ""}</div>
            <div className="locl-header-region-currency-dropdown__button-info__currency" data-testid="locl-header-region-currency-dropdown-button-currency">{defaultCurrencies?.label || ""}</div>
          </div>
          <FaChevronDown className={styles["locl-header-region-currency-dropdown__button-arrow"]}/>
        </div>
      </Dropdown>
      <InfoTooltip placement="bottom" title={CURRENCY_TOOLTIP_INFO}/>
    </div>
  )
}

export default HeaderRegionCurrencyDropdown;
